import { Component, Input, OnDestroy, OnInit, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, combineLatest } from 'rxjs';
import { debounceTime, skip } from 'rxjs/operators';
import { FieldBase } from '../fields/field-base';
import { Option } from '../fields/field-dropdown';
import { VBCUserField } from '../fields/field-vbcuser';
import { TemporaryStorageService } from './temporary-storage.service';

@Component({
  selector: 'store-dropdown-form-section',
  template: `
    <mat-expansion-panel [ngClass]="{ 'form-hidden': !expandable }" [expanded]="startOpen" class="display">
      <mat-expansion-panel-header [collapsedHeight]="'56px'" [expandedHeight]="'auto'">
        <mat-panel-title>
          <div class="dropdown-form-header">
            <mat-icon
              [ngClass]="{
                invalid: iconName === 'warning',
                valid: iconName === 'check_circle',
                question: iconName === 'help_outline',
              }"
              >
              {{ iconName }}
            </mat-icon>
            @if (!displayAutoTitle) {
              <div class="title">{{ titleText }}</div>
            }
            @if (displayAutoTitle) {
              <div
                [ngClass]="{ valid: iconName === 'check_circle', invalid: iconName === 'warning' }"
                >
                <div class="title">{{ titleText }}</div>
                <div class="auto-title">
                  <ng-container>{{ autoTitleText }}</ng-container>
                </div>
              </div>
            }
          </div>
        </mat-panel-title>
        <mat-panel-description class="description">
          @if (displayAutoDescription) {
            <div class="description-text" [ngClass]="{ invalid: iconName === 'warning' }">
              {{ autoDescriptionText }}
            </div>
          }
          @if (!displayAutoDescription) {
            <div class="description-text" [ngClass]="{ invalid: iconName === 'warning' }">
              {{ descriptionText }}
            </div>
          }
        </mat-panel-description>
      </mat-expansion-panel-header>
      <div class="expansion-panel-body">
        @for (field of fields; track field) {
          <store-order-field [field]="field" [form]="dropDownForm"></store-order-field>
        }
        @if (editingHint !== '') {
          <p class="editing-hint">
            <i>{{ editingHint }}</i>
          </p>
        }
      </div>
      @if (bottomActionsContent) {
        <ng-container [ngTemplateOutlet]="bottomActionsContent"></ng-container>
      }
    </mat-expansion-panel>
    <div class="print">
      <div class="title">{{ titleText }}</div>
      @for (field of fields; track field) {
        <store-order-field [field]="field" [form]="dropDownForm"></store-order-field>
      }
    </div>
    `,
  styleUrls: ['./dropdown-form-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DropDownFormSectionComponent implements OnInit, OnDestroy {
  @Input() prepopulatedData: object;
  @Input() startOpen = false;
  @Input() parentForm: UntypedFormGroup;
  @Input() displayAutoTitle = false;
  @Input() titleText = '';
  @Input() displayAutoDescription = true;
  @Input() descriptionText = '';
  @Input() fields: FieldBase<any>[];
  @Input() editingHint = '';
  @Input() expandable = true;
  @Input() temporaryStorageKey = '';
  @Input() bottomActionsContent: TemplateRef<any>;

  autoDescriptionText = '';
  iconName = 'help_outline';
  subscriptions: Subscription[] = [];
  uniqueIndex = 1;
  autoTitleText = '';
  contactIdField: VBCUserField;
  protected dropDownForm: UntypedFormGroup;

  constructor(
    private translateService: TranslateService,
    private temporaryStorage: TemporaryStorageService,
  ) {}

  ngOnInit(): void {
    const allFieldsHidden = this.fields.reduce((p, c) => p && c.hidden, true);
    if (allFieldsHidden) {
      this.expandable = false;
    }
    while (Object.prototype.hasOwnProperty.call(this.parentForm.controls, this.titleText + this.uniqueIndex)) {
      this.uniqueIndex += 1;
    }
    this.dropDownForm = this.toFormGroup(this.fields);
    this.parentForm.addControl(this.titleText + this.uniqueIndex, this.dropDownForm);
    const form = this.parentForm.controls[this.titleText + this.uniqueIndex] as UntypedFormGroup;

    // Check if a field with id 'contact_id' exists
    // This is only used within the common form data when the vendor has enabled the "contact" data fields
    // If the business has more than a single smb user then a dynamic dropdown will be displayed
    // Show the form as open so that the data can be selected
    this.contactIdField = this.fields.find((field) => field.id === 'contact_id') as VBCUserField;

    // set form values with prepopulated field data
    for (const key in form.controls) {
      if (Object.prototype.hasOwnProperty.call(form.controls, key)) {
        if (this.prepopulatedData != null && Object.prototype.hasOwnProperty.call(this.prepopulatedData, key)) {
          form.controls[key].setValue(this.prepopulatedData[key]);
        }
      }
    }

    // temporary storage key to use for session storage
    if (this.temporaryStorageKey != '') {
      // check to see if an existing value exists in the temporary storage
      const existingFormData = this.temporaryStorage.get(this.temporaryStorageKey);
      if (existingFormData) {
        for (const key in form.controls) {
          // if the form control exists check to see if it has an existing value (that was previously saved)
          const field = this.fields.find((f) => f.id === key);
          if (field?.value) {
            form.controls[key].setValue(field.value);
          } else {
            // there is no existing value set the value from the temporary storage
            if (Object.prototype.hasOwnProperty.call(form.controls, key)) {
              if (Object.prototype.hasOwnProperty.call(existingFormData, key)) {
                form.controls[key].setValue(existingFormData[key]);
              }
            }
          }
        }
      }

      // save the latest form values to temporary storage
      this.subscriptions.push(
        form.valueChanges.pipe(skip(1), debounceTime(300)).subscribe((value) => {
          this.temporaryStorage.set(this.temporaryStorageKey, value);
        }),
      );
    }

    this.subscriptions.push(
      combineLatest([form.statusChanges, this.translateService.stream('FRONTEND.STORE.AUTO_TITLE_TEXT')]).subscribe(
        ([change, autoTitleText]) => {
          let missingFields = false;
          for (const control in form.controls) {
            if (this.isFormControlEmpty(form, control)) {
              this.iconName = 'help_outline';
              missingFields = true;
              if (change === 'INVALID') {
                this.autoTitleText = autoTitleText.SOME_FIELDS_INVALID;
                this.startOpen = true;
                if (form.controls[control].dirty || form.controls[control].touched) {
                  this.iconName = 'warning';
                  break;
                }
              } else {
                this.autoTitleText = autoTitleText.OPTIONAL_FIELDS_UNANSWERED;
              }
            }
          }
          if (!missingFields) {
            this.iconName = 'check_circle';
            this.autoTitleText = autoTitleText.COMPLETE;
          }
        },
      ),
    );

    // The drop down form section is used by the common form data section as well as vendor order forms
    // The following code handles the "contact_id" fields which is part of the common order form data
    // TODO: refactor the common order form data into dedicated components
    if (this.contactIdField) {
      this.subscriptions.push(
        form.get('contact_id').valueChanges.subscribe((contactIdValue) => {
          const selectedContact = JSON.parse(contactIdValue);

          if (selectedContact.firstName) {
            let fullName = selectedContact.firstName;
            if (selectedContact.lastName) {
              fullName += ' ' + selectedContact.lastName;
            }
            form.controls['contact_name'].setValue(fullName, { emitEvent: true });
          }

          if (selectedContact.email) {
            form.controls['contact_email'].setValue(selectedContact.email, { emitEvent: true });
          }

          if (selectedContact.phoneNumber) {
            form.controls['contact_phone_number'].setValue(selectedContact.phoneNumber, { emitEvent: true });
          }
        }),
      );
    }

    this.subscriptions.push(
      form.valueChanges.subscribe((changes) => {
        let description = '';
        for (const key in changes) {
          // As per above we need to skip change detection for the contact_id field
          if (key === 'contact_id') {
            continue;
          }

          if (Object.prototype.hasOwnProperty.call(changes, key) && changes[key] != null && changes[key].length > 0) {
            if (changes[key][0].name) {
              for (const fileKey in changes[key]) {
                if (changes[key][fileKey] != null) {
                  description += changes[key][fileKey].name;
                  description += ', ';
                }
              }
            } else {
              description += changes[key];
              description += ', ';
            }
          }
        }
        if (this.displayAutoDescription) {
          this.autoDescriptionText = description.substring(0, description.length - 2);
        }
      }),
    );

    if (this.contactIdField) {
      this.startOpen = true;
      const contacts = this.contactIdField.options as Option[];
      const validContact = contacts.find((contact) => {
        if (contact?.disabled) {
          return false;
        }
        const currentContact = JSON.parse(contact.value);
        return currentContact.email === form.controls['contact_email'].value;
      });
      if (validContact) {
        form.controls['contact_id'].setValue(validContact.value, { emitEvent: true });
      }
    }

    form.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  toFormGroup(formFields: FieldBase<any>[]): UntypedFormGroup {
    const group: any = {};
    formFields.forEach((field) => {
      let validations;
      if (!field.required || field.hidden) {
        validations = field.validator;
      } else if (field.controlType === 'checkbox') {
        validations = [Validators.requiredTrue, field.validator];
      } else {
        validations = [Validators.required, field.validator];
      }

      const formControl = new UntypedFormControl({ value: field.value, disabled: field.disabled }, validations);
      group[field.id] = formControl;
      this.subscriptions.push(formControl.valueChanges.subscribe((value) => (field.value = value)));
    });

    return new UntypedFormGroup(group);
  }

  isFormControlEmpty(form: UntypedFormGroup, control: string): boolean {
    // hidden fields should not need to be filled in
    if (this.fields.find((field) => field.id === control)?.hidden) {
      return false;
    }

    return (
      !form.controls[control].value ||
      (form.controls[control].value.constructor === Array && !form.controls[control].value[0])
    );
  }

  ngOnDestroy(): void {
    this.parentForm.removeControl(this.titleText + this.uniqueIndex);
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
